<template>
    <section class="timesheet-history-view">
        <b-alert show variant="info">
            <template v-if="nativePilot">Пилотный табель · только просмотр. Часы посчитаны по сохранённым дневным отметкам.</template>
            <template v-else>{{ archiveLabel(sheet.timesheet) }}. Закрытый снимок исходной таблицы: строки, отметки и итоговые часы/дни сохранены без пересчёта по текущим правилам.</template>
            <a v-if="sourceUrl" :href="sourceUrl" target="_blank" rel="noopener noreferrer">Открыть источник</a>
        </b-alert>
        <label v-if="sources.length > 1" class="timesheet-history-view__sources">Архивный табель
            <b-form-select :value="Number(sheet.timesheet.id)" :options="sourceOptions" :disabled="disabled" @input="$emit('select', $event)" />
        </label>
        <div class="table-responsive">
            <table class="table table-sm table-bordered">
                <thead><tr><th>{{ nativePilot ? 'Сотрудник' : 'Сотрудник в источнике' }}</th><th v-for="day in days" :key="day">{{ day }}</th><th>{{ nativePilot ? 'Дни' : 'Дни источника' }}</th><th>{{ nativePilot ? 'Часы' : 'Часы источника' }}</th><th v-if="!nativePilot">Часы календаря</th><th>КТУ</th><th>Характеристики</th></tr></thead>
                <tbody><tr v-for="line in sheet.lines" :key="line.id" :data-review-line-id="line.id" :class="{'employee-focused-row': focusedLineIds.includes(Number(line.id))}">
                    <th scope="row">
                        <button v-if="line.employee_id || line.resolved_employee_id" type="button" class="btn btn-link p-0 text-left" @click="$emit('open-employee', linkedLine(line))">{{ line.employee_name_snapshot }}</button>
                        <span v-else>{{ line.employee_name_snapshot }}</span>
                        <small v-if="line.unresolved_person_id && !line.resolved_employee_id" class="d-block text-muted">Нет связи с карточкой</small>
                        <small v-if="line.resolved_employee_id" class="d-block text-muted">Связь подтверждена позднее</small>
                        <small v-if="line.source_payload.source_row" class="d-block text-muted">Строка {{ line.source_payload.source_row }} в своде</small>
                    </th>
                    <td v-for="day in days" :key="day" :title="cell(line, day)">{{ cell(line, day) }}</td>
                    <td>{{ total(line, 'equivalent_days') }}</td><td>{{ total(line, 'worked_minutes', 60) }}</td>
                    <td v-if="!nativePilot">{{ total(line, 'worked_minutes', 60, 'calendar_totals') }}
                        <details v-if="line.invalid_calendar_days && line.invalid_calendar_days.length"><summary>Вне месяца</summary>
                            <span v-for="item in line.invalid_calendar_days" :key="item.day" class="d-block">{{ item.day }}: {{ item.source_value }}</span>
                        </details>
                    </td><td>{{ line.ktu === null || line.ktu === undefined ? '—' : line.ktu }}</td>
                    <td class="timesheet-history-view__characteristics"><details v-if="characteristics(line).length"><summary>Показать</summary>
                        <span v-for="item in characteristics(line)" :key="item.key" class="d-block">{{ item.label }}: {{ item.value }}</span>
                    </details><span v-else>—</span></td>
                </tr></tbody>
            </table>
        </div>
        <p v-if="!nativePilot" class="small text-muted">«—» в итогах означает, что значение отсутствовало в источнике. Часы календаря учитывают только существующие даты месяца. Несколько строк одного сотрудника сохранены отдельно.</p>
    </section>
</template>

<script>
import {archiveLabel} from './timesheetArchiveLabel';
export default {
    name: 'TimesheetHistoryView',
    props: {sheet: {type: Object, required: true}, sources: {type: Array, default: () => []}, disabled: Boolean, focusedLineIds: {type: Array, default: () => []}},
    computed: {
        nativePilot() { return this.sheet.timesheet.record_kind === 'pilot' && this.sheet.timesheet.source !== 'legacy_google' && this.sheet.timesheet.status !== 'historical_locked'; },
        days() { return Array.from({length: Number(String(this.sheet.period.ends_on).slice(8, 10))}, (_, index) => index + 1); },
        entryIndex() {
            const result = new Map();
            this.sheet.lines.forEach(line => (line.entries || []).forEach(entry => {
                result.set(`${line.id}:${Number(String(entry.work_date).slice(8, 10))}`, entry);
            }));
            return result;
        },
        sourceOptions() { return this.sources.map(row => ({value: Number(row.id), text: archiveLabel(row)})); },
        sourceUrl() { const url = this.sheet.timesheet.source_payload && this.sheet.timesheet.source_payload.source_file_url; return /^https:\/\/docs\.google\.com\//.test(url || '') ? url : null; }
    },
    methods: {
        archiveLabel,
        linkedLine(line) { return {...line, employee_id: line.employee_id || line.resolved_employee_id}; },
        total(line, key, divisor = 1, field = 'totals') {
            const value = line[field] && line[field][key];
            return value === null || value === undefined ? '—' : (Number(value) / divisor).toLocaleString('ru-RU', {maximumFractionDigits: 3});
        },
        characteristics(line) {
            const labels = {business_trip_readiness: 'Командировки', learning_ability: 'Обучаемость',
                responsibility: 'Ответственность', performance_note: 'Комментарий'};
            const values = line.characteristic_values || {};
            return Object.keys(values).map(key => ({key, label: labels[key] || key,
                value: values[key] === true ? 'Да' : values[key] === false ? 'Нет' : values[key] === null ? '—' : String(values[key])}));
        },
        cell(line, day) {
            const entry = this.entryIndex.get(`${line.id}:${day}`);
            if (!entry) return '';
            if (entry.source_value !== null && entry.source_value !== undefined) return String(entry.source_value);
            const codes = this.sheet.codes || [];
            return [Number(entry.worked_minutes) > 0 ? Number(entry.worked_minutes) / 60 : '',
                ...['absence_code_id', 'travel_code_id', 'other_code_id'].map(key => {
                    const code = codes.find(value => Number(value.id) === Number(entry[key]));
                    return code ? code.code : '';
                })].filter(value => value !== '').join(' ');
        }
    }
};
</script>

<style scoped>
.timesheet-history-view { margin-top: 16px; }
.timesheet-history-view__sources { display: block; max-width: 520px; margin-bottom: 12px; }
.timesheet-history-view table { font-variant-numeric: tabular-nums; }
.timesheet-history-view td, .timesheet-history-view thead th { text-align: center; white-space: nowrap; min-width: 38px; }
.timesheet-history-view tbody th { min-width: 190px; font-weight: normal; }
.timesheet-history-view .btn-link { font-size: inherit; }
.timesheet-history-view .timesheet-history-view__characteristics { white-space: normal; min-width: 170px; text-align: left; }
.timesheet-history-view summary { cursor: pointer; }
</style>
